import { useAtomValue } from 'jotai';
import { CtaButtonType, CtaForm } from '@pairy/sheard/types';

import { SetState } from '../../../types/helper';
import { currentPageAtom, thumbnailsAtom } from '../../../store/index.atom';
import { BulkFileType, UserFileInfoType } from '../../../types/file';
import MobileFreeBadge from '../../@common/FreeBadge/Mobile';
import MobileIntro from '../../@common/Intro/Mobile';
import MobilePage from '../../@common/Viewer/Mobile';
import MobilePageChangeEffectLayer from '../../@common/Layer/MobilePageChangeEffectLayer';
import MobilePageWrapper from '../../@common/PageWrapper/Mobile';
import MobilePageNavigationButton from '../../@common/PageNavigationButton/Mobile';
import MobileViewerLayer from '../../@common/Layer/DefaultViewerLayer/Mobile';
import { MessageBoxItemType } from '../../PdfViewer/index.types';
import ZoomableViewer from '../../@common/ZoomableViewer';

import CtaFormLayer from './CtaFormLayer';
import CtaButtonLayer from './CtaButtonLayer';
import AnnotationLayer from './AnnotationLayer';

interface Props {
  file: BulkFileType;
  user: UserFileInfoType;
  translateKoToEn: (item: string) => string;
  updateNextThumbnails: (page: number) => void;
  updatePrevThumbnails: (page: number) => void;
  setIsMobileDrawerOpen: SetState<boolean>;
  ctaForm?: CtaForm;
  ctaButtonList?: CtaButtonType[];
  messageBoxItems?: MessageBoxItemType[];
  isPreview?: boolean;
  isMobileLandscape: boolean;
}

const BulkMobileViewer = ({
  file,
  user,
  ctaForm,
  ctaButtonList,
  messageBoxItems,
  translateKoToEn,
  setIsMobileDrawerOpen,
  updateNextThumbnails,
  updatePrevThumbnails,
  isPreview = false,
  isMobileLandscape,
}: Props) => {
  const currentPage = useAtomValue(currentPageAtom);
  const thumbnails = useAtomValue(thumbnailsAtom);

  const { domain, plan } = user;
  const {
    name,
    isDownloadEnabled,
    isEmailVerificationEnabled,
    isFormSubmitted,
    pages,
  } = file;

  const ctaFormPage = ctaForm?.page;
  const blindMode = ctaForm?.blind;

  const isNextPageButtonDisabled =
    currentPage >= Number(ctaFormPage) &&
    blindMode === 'startFrom' &&
    !isFormSubmitted;

  const isRedirectCtaFormAppropriate = Boolean(
    !(blindMode === 'none') && !isFormSubmitted,
  );

  /*첫 번째 페이지에 폼이 설정되어 있는 경우에 폼 제출 이후에 보여지도록 한다 */
  const isIntroVisible =
    ctaFormPage === 1 && blindMode === 'startFrom' ? isFormSubmitted : true;

  return (
    <>
      {isIntroVisible && <MobileIntro translateKoToEn={translateKoToEn} />}
      <MobilePageWrapper
        totalPage={Number(pages)}
        isNextPageButtonDisabled={isNextPageButtonDisabled}
        updateNextThumbnails={updateNextThumbnails}
        updatePrevThumbnails={updatePrevThumbnails}
      >
        {!isPreview && (
          <MobileViewerLayer
            fileName={name}
            domain={domain}
            isBulk
            isDownloadEnabled={isDownloadEnabled}
            ctaFormPage={ctaFormPage}
            translateKoToEn={translateKoToEn}
            isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
            isMobileLandscape={isMobileLandscape}
          />
        )}

        <ZoomableViewer isMobile>
          <MobilePage
            thumnails={thumbnails?.map((value) => {
              if (value?.thumbnail) {
                return {
                  src: value.thumbnail,
                  imageSources: value.imageSources,
                };
              }
              return null;
            })}
            isMobileLandscape={isMobileLandscape}
          />
          <MobilePageChangeEffectLayer />

          {thumbnails && (
            <AnnotationLayer
              thumbnails={thumbnails}
              ctaForm={ctaForm}
              messageBoxItems={messageBoxItems}
              isPreview={isPreview}
              isFormSubmitted={isFormSubmitted}
            />
          )}
        </ZoomableViewer>
        <MobilePageNavigationButton
          totalPage={Number(pages)}
          ctaFormPage={ctaFormPage}
          isNextPageButtonDisabled={isNextPageButtonDisabled}
          isMoveCtaFormPageRequirements={
            blindMode === 'startFrom' && !isFormSubmitted
          }
          updateNextThumbnails={updateNextThumbnails}
          updatePrevThumbnails={updatePrevThumbnails}
          openDrawer={() => setIsMobileDrawerOpen(true)}
          isMobileLandscape={isMobileLandscape}
        />

        <CtaFormLayer
          ctaForm={ctaForm}
          isPreview={isPreview}
          totalPage={Number(pages)}
          isNextPageButtonDisabled={isNextPageButtonDisabled}
          isEmailVerificationEnabled={isEmailVerificationEnabled}
          isFormSubmitted={isFormSubmitted}
          translateKoToEn={translateKoToEn}
          userSubscriptionPlan={plan}
        />
        <CtaButtonLayer ctaButtonList={ctaButtonList} isPreview={isPreview} />

        <MobileFreeBadge userSubscriptionPlan={plan} />
      </MobilePageWrapper>
    </>
  );
};

export default BulkMobileViewer;
